/**
 * 额度消耗明细页
 *
 * 品牌筛选 + 服务端分页 DataGrid，展示每个品牌的额度消耗记录。
 * 数据来源：GET /admin/quota-logs
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material'
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { AdminLayout } from '@/components/AdminLayout'
import { getBrands, getQuotaLogs, type QuotaLogItem } from '@/api/services'
import type { Brand } from '@chaoyinli/shared'
import { formatDate } from '@/utils/format'

/** 额度类型中文映射 */
const QUOTA_TYPE_LABELS: Record<QuotaLogItem['quota_type'], string> = {
  generate: '新建',
  text: '换文案',
  image: '换图',
  all: '全换',
}

export function QuotaLogsPage() {
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandFilter, setBrandFilter] = useState('')
  const [rows, setRows] = useState<QuotaLogItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })

  // 加载品牌下拉
  useEffect(() => {
    getBrands()
      .then(setBrands)
      .catch(() => setBrands([]))
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getQuotaLogs({
        brand_id: brandFilter || undefined,
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      })
      setRows(data.items)
      setTotal(data.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载额度明细失败')
    } finally {
      setLoading(false)
    }
  }, [brandFilter, paginationModel])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const columns: GridColDef[] = [
    {
      field: 'brand_name',
      headerName: '品牌',
      width: 160,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'employee_name',
      headerName: '员工',
      width: 140,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'quota_type',
      headerName: '类型',
      width: 120,
      renderCell: (params) => QUOTA_TYPE_LABELS[params.value as QuotaLogItem['quota_type']] ?? '-',
    },
    {
      field: 'consumed',
      headerName: '消耗点数',
      width: 120,
      type: 'number',
      renderCell: (params) => params.value ?? 0,
    },
    {
      field: 'created_at',
      headerName: '时间',
      width: 200,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'note_id',
      headerName: '笔记ID',
      width: 220,
      renderCell: (params) => params.value || '-',
    },
  ]

  return (
    <AdminLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          额度消耗明细
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>筛选品牌</InputLabel>
          <Select
            label="筛选品牌"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
          >
            <MenuItem value="">全部品牌</MenuItem>
            {brands.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          minHeight: 400,
          backgroundColor: '#fff',
          borderRadius: '12px',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF8C00' }} />
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#fafafa' },
            }}
          />
        )}
      </Box>
    </AdminLayout>
  )
}

export default QuotaLogsPage
